import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LogOut, Eye, Mail, Phone, Building, Calendar, AlertCircle, CheckCircle, Clock, RefreshCw, AlertTriangle, Trash2, Bell, Shield, Users, BarChart3, Beaker, Database, Thermometer, Newspaper, Send, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { getApiBase } from '../lib/apiBase';


const API_URL = getApiBase();

export const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [storedPassword, setStoredPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [retentionInfo, setRetentionInfo] = useState(null);
  const [showRetentionPanel, setShowRetentionPanel] = useState(false);
  const [adminTab, setAdminTab] = useState('contacts');
  const [itUsers, setItUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [thermalCalcs, setThermalCalcs] = useState([]);
  const [thermalContacts, setThermalContacts] = useState([]);
  const [durabilityCalcs, setDurabilityCalcs] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [calculatorLookups, setCalculatorLookups] = useState([]);
  const [rateLimitSettings, setRateLimitSettings] = useState(null);
  const [ipLocationCache, setIpLocationCache] = useState({});

  // Check if already logged in
  useEffect(() => {
    const savedPassword = sessionStorage.getItem('adminPassword');
    if (savedPassword) {
      setStoredPassword(savedPassword);
      setIsAuthenticated(true);
      fetchSubmissions(savedPassword);
      fetchRetentionInfo(savedPassword);
      fetchItUsers(savedPassword);
      fetchThermalData(savedPassword);
      fetchSubscribers(savedPassword);
      fetchCalculatorLookups(savedPassword);
      fetchRateLimitSettings(savedPassword);
    }
  }, []);

  // Re-fetch rate-limit settings whenever the Rate Limits tab becomes active.
  // This clears any stale "backend not deployed" error after a redeploy without
  // requiring the user to click Retry manually.
  useEffect(() => {
    if (adminTab === 'rate-limits' && storedPassword) {
      fetchRateLimitSettings(storedPassword);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTab, storedPassword]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        sessionStorage.setItem('adminPassword', password);
        setStoredPassword(password);
        setIsAuthenticated(true);
        fetchSubmissions(password);
        fetchRetentionInfo(password);
        fetchItUsers(password);
        fetchThermalData(password);
        fetchSubscribers(password);
        fetchCalculatorLookups(password);
        fetchRateLimitSettings(password);
      } else {
        setError('Invalid password. Please try again.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async (pwd) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/submissions?password=${encodeURIComponent(pwd)}`);
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data);
      }
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
    }
  };

  const fetchRetentionInfo = async (pwd) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/retention-info?password=${encodeURIComponent(pwd)}`);
      if (response.ok) {
        const data = await response.json();
        setRetentionInfo(data);
      }
    } catch (err) {
      console.error('Failed to fetch retention info:', err);
    }
  };

  const fetchItUsers = async (pwd) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/it-users?password=${encodeURIComponent(pwd)}`);
      if (response.ok) {
        const data = await response.json();
        setItUsers(data);
      }
    } catch (err) {
      console.error('Failed to fetch IT users:', err);
    }
  };

  const fetchThermalData = async (pwd) => {
    try {
      const [calcsRes, contactsRes, durRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/thermal-calculations?password=${encodeURIComponent(pwd)}`),
        fetch(`${API_URL}/api/admin/thermal-contacts?password=${encodeURIComponent(pwd)}`),
        fetch(`${API_URL}/api/admin/durability-calculations?password=${encodeURIComponent(pwd)}`),
      ]);
      if (calcsRes.ok) setThermalCalcs(await calcsRes.json());
      if (contactsRes.ok) setThermalContacts(await contactsRes.json());
      if (durRes.ok) setDurabilityCalcs(await durRes.json());
    } catch (err) {
      console.error('Failed to fetch thermal/durability data:', err);
    }
  };

  const fetchSubscribers = async (pwd) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/subscribers?password=${encodeURIComponent(pwd)}`);
      if (response.ok) setSubscribers(await response.json());
    } catch (err) {
      console.error('Failed to fetch subscribers:', err);
    }
  };

  const fetchCalculatorLookups = async (pwd) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/calculator-lookups?password=${encodeURIComponent(pwd)}&limit=500`);
      if (r.ok) {
        const rows = await r.json();
        setCalculatorLookups(rows);
        // v17: resolve IP → city/country for any IP not yet cached.
        // ipapi.co free tier is CORS-friendly; we just fire-and-forget per
        // unique IP. Cache lives in component state for the session.
        const uniq = [...new Set(rows.map(r => r.ip || r.client_ip).filter(Boolean))];
        uniq.forEach((ip) => {
          setIpLocationCache((prev) => {
            if (prev[ip]) return prev;
            fetch(`https://ipapi.co/${ip}/json/`)
              .then(r => r.ok ? r.json() : null)
              .then(d => {
                if (!d || d.error) return;
                const parts = [d.city, d.region, d.country_name].filter(Boolean);
                setIpLocationCache((p) => ({ ...p, [ip]: parts.join(', ') || 'Unknown' }));
              })
              .catch(() => {});
            return { ...prev, [ip]: '…' };  // loading marker
          });
        });
      }
    } catch (err) {
      console.error('Failed to fetch calculator lookups:', err);
    }
  };

  // v17: rate-limit admin settings
  const [rateLimitError, setRateLimitError] = useState(null);
  const [rateLimitUpdating, setRateLimitUpdating] = useState(null); // which calc is being saved
  const fetchRateLimitSettings = async (pwd) => {
    setRateLimitError(null);
    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort(), 12000);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/rate-limits?password=${encodeURIComponent(pwd)}`,
        { signal: ctrl.signal }
      );
      clearTimeout(timeoutId);
      if (r.ok) {
        setRateLimitSettings(await r.json());
        return;
      }
      if (r.status === 404) {
        setRateLimitError(
          'This backend does not have the rate-limit endpoint yet. The preview has it — please redeploy to production.'
        );
      } else if (r.status === 401) {
        setRateLimitError('Invalid admin password — log out and log in again.');
      } else {
        const text = await r.text().catch(() => '');
        setRateLimitError(`Server returned HTTP ${r.status}${text ? `: ${text.slice(0, 200)}` : ''}.`);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('Failed to fetch rate-limit settings:', err);
      if (err.name === 'AbortError') {
        setRateLimitError('Request timed out after 12s. Backend may be unreachable or overloaded.');
      } else {
        setRateLimitError(`Network error: ${err.message || 'could not reach backend'}.`);
      }
    }
  };

  const updateRateLimit = async (calculator, patch) => {
    setRateLimitError(null);
    setRateLimitUpdating(calculator);
    // Optimistic update so the toggle visually flips immediately.
    const previous = rateLimitSettings;
    if (rateLimitSettings && rateLimitSettings[calculator]) {
      setRateLimitSettings({
        ...rateLimitSettings,
        [calculator]: { ...rateLimitSettings[calculator], ...patch },
      });
    }
    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort(), 12000);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/rate-limits/${calculator}?password=${encodeURIComponent(storedPassword)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
          signal: ctrl.signal,
        }
      );
      clearTimeout(timeoutId);
      if (r.ok) {
        const updated = await r.json();
        setRateLimitSettings(updated);
        return;
      }
      // Roll back optimistic update on failure.
      setRateLimitSettings(previous);
      const text = await r.text().catch(() => '');
      setRateLimitError(
        `Couldn't save ${calculator}: HTTP ${r.status}${text ? ` – ${text.slice(0, 200)}` : ''}.`
      );
    } catch (err) {
      clearTimeout(timeoutId);
      setRateLimitSettings(previous);
      console.error('Failed to update rate-limit setting:', err);
      setRateLimitError(
        err.name === 'AbortError'
          ? `Save for ${calculator} timed out after 12s.`
          : `Network error saving ${calculator}: ${err.message || 'unknown'}.`
      );
    } finally {
      setRateLimitUpdating(null);
    }
  };

  const updateStatus = async (submissionId, newStatus) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/submissions/${submissionId}?password=${encodeURIComponent(storedPassword)}&status=${newStatus}`,
        { method: 'PATCH' }
      );
      if (response.ok) {
        fetchSubmissions(storedPassword);
        if (selectedSubmission?.id === submissionId) {
          setSelectedSubmission({ ...selectedSubmission, status: newStatus });
        }
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const markAsNotified = async (submissionId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/submissions/${submissionId}/mark-notified?password=${encodeURIComponent(storedPassword)}`,
        { method: 'PATCH' }
      );
      if (response.ok) {
        fetchRetentionInfo(storedPassword);
      }
    } catch (err) {
      console.error('Failed to mark as notified:', err);
    }
  };

  const anonymizeSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to permanently delete this person\'s data? This action cannot be undone.')) {
      return;
    }
    try {
      const response = await fetch(
        `${API_URL}/api/admin/submissions/${submissionId}/anonymize?password=${encodeURIComponent(storedPassword)}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        fetchSubmissions(storedPassword);
        fetchRetentionInfo(storedPassword);
        setSelectedSubmission(null);
      }
    } catch (err) {
      console.error('Failed to anonymize:', err);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminPassword');
    setIsAuthenticated(false);
    setStoredPassword('');
    setPassword('');
    setSubmissions([]);
    setSelectedSubmission(null);
    setRetentionInfo(null);
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700',
      reviewed: 'bg-yellow-100 text-yellow-700',
      contacted: 'bg-green-100 text-green-700',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.new}`}>
        {status || 'new'}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Admin Panel</CardTitle>
            <p className="text-gray-500 text-sm">Enter your password to access submissions</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                />
              </div>
              {error && (
                <div className="flex items-center space-x-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              )}
              <Button 
                type="submit" 
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-100 pt-24 pb-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Retention Alert Banner */}
        {retentionInfo && (retentionInfo.total_expiring_soon > 0 || retentionInfo.total_expired > 0) && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">Data Retention Notice</h3>
                <p className="text-sm text-amber-700 mt-1">
                  {retentionInfo.total_expired > 0 && (
                    <span className="font-medium text-red-600">{retentionInfo.total_expired} record(s) have exceeded the {retentionInfo.retention_years}-year retention period and should be deleted. </span>
                  )}
                  {retentionInfo.total_expiring_soon > 0 && (
                    <span>{retentionInfo.total_expiring_soon} record(s) will expire within 90 days. </span>
                  )}
                  Per GDPR/PDPO compliance, you must notify contacts before deletion.
                </p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                  onClick={() => setShowRetentionPanel(!showRetentionPanel)}
                >
                  <Shield className="w-4 h-4 mr-2" />
                  {showRetentionPanel ? 'Hide' : 'Manage'} Data Retention
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Retention Management Panel */}
        {showRetentionPanel && retentionInfo && (
          <Card className="mb-6 border-amber-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center space-x-2">
                <Shield className="w-5 h-5 text-amber-600" />
                <span>Data Retention Management</span>
              </CardTitle>
              <p className="text-sm text-gray-500">
                Data is retained for {retentionInfo.retention_years} years per GDPR/PDPO. Contacts must be notified before deletion.
              </p>
            </CardHeader>
            <CardContent>
              {/* Expired Records */}
              {retentionInfo.expired.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-red-700 mb-2 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Expired - Requires Immediate Action ({retentionInfo.expired.length})
                  </h4>
                  <div className="space-y-2">
                    {retentionInfo.expired.map((item) => (
                      <div key={item.id} className="flex items-center justify-between bg-red-50 p-3 rounded-lg">
                        <div>
                          <span className="font-medium">{item.name}</span>
                          <span className="text-sm text-gray-500 ml-2">({item.email})</span>
                          <p className="text-xs text-red-600">Expired {Math.abs(item.days_until_expiry)} days ago</p>
                        </div>
                        <div className="flex space-x-2">
                          {!item.retention_notified && (
                            <Button size="sm" variant="outline" onClick={() => markAsNotified(item.id)}>
                              <Bell className="w-3 h-3 mr-1" />
                              Mark Notified
                            </Button>
                          )}
                          <Button size="sm" variant="destructive" onClick={() => anonymizeSubmission(item.id)}>
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete Data
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expiring Soon */}
              {retentionInfo.expiring_soon.length > 0 && (
                <div>
                  <h4 className="font-medium text-amber-700 mb-2 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Expiring Soon - Notify Contact ({retentionInfo.expiring_soon.length})
                  </h4>
                  <div className="space-y-2">
                    {retentionInfo.expiring_soon.map((item) => (
                      <div key={item.id} className="flex items-center justify-between bg-amber-50 p-3 rounded-lg">
                        <div>
                          <span className="font-medium">{item.name}</span>
                          <span className="text-sm text-gray-500 ml-2">({item.email})</span>
                          <p className="text-xs text-amber-600">
                            Expires in {item.days_until_expiry} days (on {new Date(item.expiry_date).toLocaleDateString()})
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          {item.retention_notified ? (
                            <span className="text-xs text-green-600 flex items-center">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Notified
                            </span>
                          ) : (
                            <>
                              <a href={`mailto:${item.email}?subject=Data Retention Notice - Durable Designs&body=Dear ${item.name},%0D%0A%0D%0AAs per GDPR/PDPO data protection regulations, we are writing to inform you that your contact information submitted to Durable Designs will be deleted on ${new Date(item.expiry_date).toLocaleDateString()} (${retentionInfo.retention_years} years after submission).%0D%0A%0D%0AIf you wish to continue receiving communications from us, please reply to this email or submit a new inquiry through our website.%0D%0A%0D%0ABest regards,%0D%0ADurable Designs`}>
                                <Button size="sm" variant="outline">
                                  <Mail className="w-3 h-3 mr-1" />
                                  Send Notice
                                </Button>
                              </a>
                              <Button size="sm" variant="ghost" onClick={() => markAsNotified(item.id)}>
                                Mark Notified
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {retentionInfo.expired.length === 0 && retentionInfo.expiring_soon.length === 0 && (
                <p className="text-gray-500 text-center py-4">No records require attention at this time.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-500">Manage contacts and checker users</p>
          </div>
          <div className="flex space-x-3">
            {retentionInfo && (retentionInfo.total_expiring_soon > 0 || retentionInfo.total_expired > 0) && (
              <Button 
                variant="outline" 
                onClick={() => setShowRetentionPanel(!showRetentionPanel)}
                className="flex items-center space-x-2 border-amber-300 text-amber-700"
              >
                <AlertTriangle className="w-4 h-4" />
                <span>{retentionInfo.total_expiring_soon + retentionInfo.total_expired}</span>
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => { fetchSubmissions(storedPassword); fetchRetentionInfo(storedPassword); fetchItUsers(storedPassword); fetchThermalData(storedPassword); fetchSubscribers(storedPassword); fetchCalculatorLookups(storedPassword); }}
              className="flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </Button>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="flex items-center space-x-2 text-red-600 hover:text-red-700"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          <button onClick={() => setAdminTab('contacts')} data-testid="admin-tab-contacts"
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${adminTab === 'contacts' ? 'border-green-600 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            <Mail className="w-4 h-4" /> Contact Submissions <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{submissions.length}</span>
          </button>
          <button onClick={() => setAdminTab('it-users')} data-testid="admin-tab-it-users"
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${adminTab === 'it-users' ? 'border-green-600 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            <Beaker className="w-4 h-4" /> Identity Testing Users <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">{itUsers.length}</span>
          </button>
          <button onClick={() => setAdminTab('thermal')} data-testid="admin-tab-thermal"
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${adminTab === 'thermal' ? 'border-green-600 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            <Thermometer className="w-4 h-4" /> Thermal Calculator <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-red-100 text-red-700">{thermalCalcs.length}</span>
          </button>
          <button onClick={() => setAdminTab('durability')} data-testid="admin-tab-durability"
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${adminTab === 'durability' ? 'border-green-600 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            <Shield className="w-4 h-4" /> Service Life <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">{durabilityCalcs.length}</span>
          </button>
          <button onClick={() => setAdminTab('subscribers')} data-testid="admin-tab-subscribers"
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${adminTab === 'subscribers' ? 'border-green-600 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            <Newspaper className="w-4 h-4" /> Subscribers <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">{subscribers.filter(s => s.verified).length}</span>
          </button>
          <button onClick={() => setAdminTab('lookups')} data-testid="admin-tab-lookups"
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${adminTab === 'lookups' ? 'border-green-600 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            <BarChart3 className="w-4 h-4" /> Calc. Lookups <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">{calculatorLookups.length}</span>
          </button>
        </div>

        {adminTab === 'contacts' && (
        <>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Submissions List */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                {submissions.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No submissions yet</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {submissions.map((sub) => (
                      <div
                        key={sub.id}
                        onClick={() => setSelectedSubmission(sub)}
                        className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedSubmission?.id === sub.id ? 'bg-green-50 border-l-4 border-green-600' : ''
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="font-medium text-gray-900">{sub.name}</span>
                              {getStatusBadge(sub.status)}
                            </div>
                            <p className="text-sm text-gray-500">{sub.email}</p>
                            <p className="text-sm text-gray-400 mt-1 line-clamp-1">{sub.description}</p>
                          </div>
                          <div className="text-right text-xs text-gray-400">
                            {formatDate(sub.created_at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Submission Detail */}
          <div className="lg:col-span-1">
            {selectedSubmission ? (
              <Card className="sticky top-24">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">{selectedSubmission.name}</CardTitle>
                    {getStatusBadge(selectedSubmission.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Contact Info */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <a href={`mailto:${selectedSubmission.email}`} className="text-green-600 hover:underline">
                        {selectedSubmission.email}
                      </a>
                    </div>
                    {selectedSubmission.phone && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span>{selectedSubmission.phone}</span>
                      </div>
                    )}
                    {selectedSubmission.company && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Building className="w-4 h-4 text-gray-400" />
                        <span>{selectedSubmission.company}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-sm">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>{formatDate(selectedSubmission.created_at)}</span>
                    </div>
                  </div>

                  {/* Project Details */}
                  {(selectedSubmission.structure_type || selectedSubmission.main_issue) && (
                    <div className="border-t pt-3 space-y-2">
                      {selectedSubmission.structure_type && (
                        <div>
                          <span className="text-xs text-gray-500 uppercase">Structure Type</span>
                          <p className="text-sm">{selectedSubmission.structure_type}</p>
                        </div>
                      )}
                      {selectedSubmission.main_issue && (
                        <div>
                          <span className="text-xs text-gray-500 uppercase">Main Issue</span>
                          <p className="text-sm">{selectedSubmission.main_issue}</p>
                        </div>
                      )}
                      {selectedSubmission.urgency && (
                        <div>
                          <span className="text-xs text-gray-500 uppercase">Urgency</span>
                          <p className="text-sm">{selectedSubmission.urgency}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Description */}
                  <div className="border-t pt-3">
                    <span className="text-xs text-gray-500 uppercase">Description</span>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{selectedSubmission.description}</p>
                  </div>

                  {/* Status Actions */}
                  <div className="border-t pt-3">
                    <span className="text-xs text-gray-500 uppercase mb-2 block">Update Status</span>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant={selectedSubmission.status === 'new' ? 'default' : 'outline'}
                        onClick={() => updateStatus(selectedSubmission.id, 'new')}
                        className="flex-1"
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        New
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedSubmission.status === 'reviewed' ? 'default' : 'outline'}
                        onClick={() => updateStatus(selectedSubmission.id, 'reviewed')}
                        className="flex-1"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Reviewed
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedSubmission.status === 'contacted' ? 'default' : 'outline'}
                        onClick={() => updateStatus(selectedSubmission.id, 'contacted')}
                        className="flex-1"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Contacted
                      </Button>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="border-t pt-3">
                    <a 
                      href={`mailto:${selectedSubmission.email}?subject=Re: Your inquiry to Durable Designs`}
                      className="block"
                    >
                      <Button className="w-full bg-green-600 hover:bg-green-700">
                        <Mail className="w-4 h-4 mr-2" />
                        Reply via Email
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Select a submission to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
        </>
        )}

        {adminTab === 'it-users' && (
          <ItUsersPanel users={itUsers} selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
        )}

        {adminTab === 'thermal' && (
          <div className="space-y-6">
            {/* Specialist Contact Requests */}
            {thermalContacts.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Mail className="w-5 h-5 text-red-600" /> Specialist Requests ({thermalContacts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {thermalContacts.map((c, i) => (
                      <div key={i} className="flex items-start justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{c.name}</p>
                          <p className="text-xs text-gray-500">{c.email}</p>
                          {c.message && <p className="text-xs text-gray-600 mt-1">{c.message}</p>}
                          <p className="text-xs text-gray-400 mt-1">{formatDate(c.created_at)}</p>
                        </div>
                        <a href={`mailto:${c.email}?subject=Thermal Analysis - Specialist Review`}>
                          <Button size="sm" variant="outline" className="text-xs"><Mail className="w-3 h-3 mr-1" /> Reply</Button>
                        </a>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Calculation Log */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Thermometer className="w-5 h-5 text-emerald-600" /> Calculation Log ({thermalCalcs.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {thermalCalcs.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Thermometer className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No calculations yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Date</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">City</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Mix</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Thickness</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">T place</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">T max</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Diff</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Flags</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">IP Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        {thermalCalcs.map((c, i) => {
                          const cementLabel = c.cement_type === 'CEM_I' ? 'CEM I' : c.cement_type === 'CEM_I_FA' ? `FA ${c.addition_percent}%` : `GGBS ${c.addition_percent}%`;
                          return (
                            <tr key={i} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(c.created_at)}</td>
                              <td className="px-4 py-2.5 text-xs font-medium">{c.city_name || '—'}</td>
                              <td className="px-4 py-2.5 text-xs">{c.binder_content} kg/m³ {cementLabel}</td>
                              <td className="px-4 py-2.5 text-xs">{c.pour_thickness}mm</td>
                              <td className="px-4 py-2.5 text-xs">{c.placing_temperature}°C</td>
                              <td className="px-4 py-2.5 text-xs font-medium">{c.max_temperature}°C</td>
                              <td className="px-4 py-2.5 text-xs">{c.max_differential_top}°C</td>
                              <td className="px-4 py-2.5">
                                {(c.warning_high_temp || c.warning_high_differential) ? (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700">
                                    <AlertTriangle className="w-3 h-3" />
                                    {c.warning_high_temp && 'DEF'}{c.warning_high_temp && c.warning_high_differential && '+'}{c.warning_high_differential && 'Crack'}
                                  </span>
                                ) : (
                                  <span className="text-xs text-green-600">OK</span>
                                )}
                              </td>
                              <td className="px-4 py-2.5 text-xs text-gray-500">
                                {c.client_geo ? `${c.client_geo.city || ''}, ${c.client_geo.country || ''}` : c.client_ip || '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {adminTab === 'durability' && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" /> Service Life Estimator Log ({durabilityCalcs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {durabilityCalcs.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No calculations yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Date</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">City</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Cement</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">w/c</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Cover</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">RH</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Scenario</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Pf (50y)</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">IP Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {durabilityCalcs.map((c, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(c.created_at)}</td>
                          <td className="px-4 py-2.5 text-xs font-medium">{c.city_name || '—'}</td>
                          <td className="px-4 py-2.5 text-xs">{c.cement_type?.replace(/_/g, ' ')}</td>
                          <td className="px-4 py-2.5 text-xs">{c.wc_ratio}</td>
                          <td className="px-4 py-2.5 text-xs">{c.cover_nominal}mm</td>
                          <td className="px-4 py-2.5 text-xs">{c.rh_mean}%</td>
                          <td className="px-4 py-2.5 text-xs capitalize">{c.scenario?.replace('_', ' ') || '—'}</td>
                          <td className="px-4 py-2.5 text-xs font-medium">{c.max_pf_50yr ? `${(c.max_pf_50yr * 100).toFixed(1)}%` : '—'}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-500">
                            {c.client_geo ? `${c.client_geo.city || ''}, ${c.client_geo.country || ''}` : c.client_ip || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {adminTab === 'subscribers' && (
          <SubscribersPanel subscribers={subscribers} storedPassword={storedPassword} onRefresh={() => fetchSubscribers(storedPassword)} />
        )}

        {adminTab === 'lookups' && (
          <CalculatorLookupsPanel
            lookups={calculatorLookups}
            ipLocations={ipLocationCache}
            onRefresh={() => fetchCalculatorLookups(storedPassword)}
          />
        )}
        {adminTab === 'rate-limits' && (
          <RateLimitPanel
            settings={rateLimitSettings}
            error={rateLimitError}
            updating={rateLimitUpdating}
            onUpdate={updateRateLimit}
            onRefresh={() => fetchRateLimitSettings(storedPassword)}
          />
        )}
      </div>
    </div>
  );
};

const CalculatorLookupsPanel = ({ lookups, ipLocations = {}, onRefresh }) => {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? lookups : lookups.filter((l) => l.calculator === filter);
  const counts = lookups.reduce((acc, l) => { acc[l.calculator] = (acc[l.calculator] || 0) + 1; return acc; }, {});
  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso.slice(0, 19); }
  };
  const calcBadge = (c) => ({
    corrosivity: 'bg-emerald-100 text-emerald-800',
    carbonation: 'bg-blue-100 text-blue-800',
    thermal:     'bg-orange-100 text-orange-800',
  })[c] || 'bg-neutral-100 text-neutral-800';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Filter:</span>
          {['all', 'corrosivity', 'carbonation', 'thermal'].map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`lookups-filter-${c}`}
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                filter === c ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {c === 'all' ? `All (${lookups.length})` : `${c} (${counts[c] || 0})`}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="lookups-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No calculator lookups recorded yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="lookups-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Time (UTC)</th>
                    <th className="text-left px-4 py-2.5 font-medium">Calculator</th>
                    <th className="text-left px-4 py-2.5 font-medium">Client IP · Location</th>
                    <th className="text-left px-4 py-2.5 font-medium">Query / Address</th>
                    <th className="text-left px-4 py-2.5 font-medium">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((l, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${calcBadge(l.calculator)}`}>
                          {l.calculator}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-700">
                        <div className="font-mono">{l.client_ip || '—'}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5" data-testid={`lookup-location-${i}`}>
                          {l.client_ip ? (ipLocations[l.client_ip] || '…') : '—'}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-900 max-w-md truncate" title={l.query}>{l.query || '—'}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-500 max-w-xs truncate" title={l.result_summary}>{l.result_summary || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const ItUsersPanel = ({ users, selectedUser, setSelectedUser }) => {
  const totalProjects = users.reduce((a, u) => a + u.project_count, 0);
  const totalPours = users.reduce((a, u) => a + u.pour_count, 0);
  const totalSamples = users.reduce((a, u) => a + u.sample_count, 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const trialStatus = (trialExp) => {
    if (!trialExp) return { label: 'Unknown', cls: 'bg-gray-100 text-gray-600' };
    const days = Math.ceil((new Date(trialExp) - new Date()) / 86400000);
    if (days <= 0) return { label: 'Expired', cls: 'bg-red-100 text-red-700' };
    if (days <= 7) return { label: `${days}d left`, cls: 'bg-amber-100 text-amber-700' };
    return { label: `${days}d left`, cls: 'bg-green-100 text-green-700' };
  };

  return (
    <div data-testid="it-users-panel">
      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900" data-testid="stat-users">{users.length}</p>
              <p className="text-xs text-gray-500">Registered Users</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Database className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900" data-testid="stat-projects">{totalProjects}</p>
              <p className="text-xs text-gray-500">Total Projects</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900" data-testid="stat-pours">{totalPours}</p>
              <p className="text-xs text-gray-500">Total Pours</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Beaker className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900" data-testid="stat-samples">{totalSamples}</p>
              <p className="text-xs text-gray-500">Total Samples</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* User list */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-0">
              {users.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No identity testing users yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-medium text-gray-600">User</th>
                        <th className="text-left p-3 font-medium text-gray-600">Company</th>
                        <th className="text-center p-3 font-medium text-gray-600">Projects</th>
                        <th className="text-center p-3 font-medium text-gray-600">Samples</th>
                        <th className="text-center p-3 font-medium text-gray-600">NVs</th>
                        <th className="text-center p-3 font-medium text-gray-600">Trial</th>
                        <th className="text-left p-3 font-medium text-gray-600">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => {
                        const trial = trialStatus(u.trial_expires_at);
                        const totalNvs = (u.projects || []).reduce((a, p) => a + (p.nv_count || 0), 0);
                        return (
                          <tr key={u.id}
                            onClick={() => setSelectedUser(u)}
                            className={`border-b cursor-pointer hover:bg-gray-50 transition-colors ${selectedUser?.id === u.id ? 'bg-green-50 border-l-4 border-l-green-600' : ''}`}
                            data-testid={`it-user-row-${u.id}`}
                          >
                            <td className="p-3">
                              <div className="font-medium text-gray-900">{u.name || 'No name'}</div>
                              <div className="text-xs text-gray-400">{u.email}</div>
                            </td>
                            <td className="p-3 text-gray-600">{u.company || '-'}</td>
                            <td className="p-3 text-center font-semibold">{u.project_count}</td>
                            <td className="p-3 text-center font-semibold">{u.sample_count}</td>
                            <td className="p-3 text-center">
                              {totalNvs > 0 ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{totalNvs}</span>
                              ) : u.sample_count > 0 ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">0</span>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${trial.cls}`}>{trial.label}</span>
                            </td>
                            <td className="p-3 text-xs text-gray-400">{formatDate(u.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* User detail */}
        <div className="lg:col-span-1">
          {selectedUser ? (
            <Card className="sticky top-24" data-testid="it-user-detail">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{selectedUser.name || 'Unknown'}</CardTitle>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${trialStatus(selectedUser.trial_expires_at).cls}`}>
                    {trialStatus(selectedUser.trial_expires_at).label}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${selectedUser.email}`} className="text-green-600 hover:underline">{selectedUser.email}</a>
                  </div>
                  {selectedUser.company && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Building className="w-4 h-4 text-gray-400" />
                      <span>{selectedUser.company}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 text-sm">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Joined {formatDate(selectedUser.created_at)}</span>
                  </div>
                </div>

                <div className="border-t pt-3">
                  <span className="text-xs text-gray-500 uppercase font-medium mb-2 block">Usage Summary</span>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-2 rounded bg-gray-50">
                      <p className="text-lg font-bold text-gray-900">{selectedUser.project_count}</p>
                      <p className="text-[10px] text-gray-500 uppercase">Projects</p>
                    </div>
                    <div className="p-2 rounded bg-gray-50">
                      <p className="text-lg font-bold text-gray-900">{selectedUser.pour_count}</p>
                      <p className="text-[10px] text-gray-500 uppercase">Pours</p>
                    </div>
                    <div className="p-2 rounded bg-gray-50">
                      <p className="text-lg font-bold text-gray-900">{selectedUser.sample_count}</p>
                      <p className="text-[10px] text-gray-500 uppercase">Samples</p>
                    </div>
                  </div>
                </div>

                {selectedUser.projects && selectedUser.projects.length > 0 && (
                  <div className="border-t pt-3">
                    <span className="text-xs text-gray-500 uppercase font-medium mb-2 block">Projects</span>
                    <div className="space-y-3">
                      {selectedUser.projects.map((p) => (
                        <div key={p.id} className="p-3 border border-gray-100 rounded text-sm" data-testid={`user-project-${p.id}`}>
                          <p className="font-medium text-gray-900 mb-1">{p.project_name}</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                            <span>f<sub>ck</sub> = {p.specified_strength} MPa</span>
                            <span>{p.exposure_class}</span>
                            <span>{p.pour_count || 0} pours</span>
                            <span>{p.sample_count || 0} samples</span>
                          </div>
                          {/* Avg strength & NV count */}
                          <div className="flex gap-3 mt-1.5">
                            {p.avg_strength != null && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700" data-testid={`project-avg-${p.id}`}>
                                Avg: {p.avg_strength} MPa
                              </span>
                            )}
                            {p.nv_count > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700" data-testid={`project-nv-${p.id}`}>
                                {p.nv_count} NV
                              </span>
                            ) : p.sample_count > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700">
                                All V
                              </span>
                            ) : null}
                          </div>
                          {/* Mix Design */}
                          {p.mix_design && (
                            <div className="mt-2 pt-2 border-t border-gray-50">
                              <p className="text-[10px] uppercase font-semibold text-gray-400 mb-1">Mix Design</p>
                              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                                {p.mix_design.cement_type && <div className="col-span-2 text-gray-600">{p.mix_design.cement_type}</div>}
                                {p.mix_design.total_cementitious > 0 && (
                                  <>
                                    <span className="text-gray-400">Cement</span>
                                    <span className="text-gray-700">{p.mix_design.cement_content} kg/m³</span>
                                  </>
                                )}
                                {p.mix_design.ggbs_content > 0 && (
                                  <>
                                    <span className="text-gray-400">GGBS</span>
                                    <span className="text-gray-700">{p.mix_design.ggbs_content} kg/m³</span>
                                  </>
                                )}
                                {p.mix_design.fly_ash_content > 0 && (
                                  <>
                                    <span className="text-gray-400">Fly Ash</span>
                                    <span className="text-gray-700">{p.mix_design.fly_ash_content} kg/m³</span>
                                  </>
                                )}
                                {p.mix_design.silica_fume_content > 0 && (
                                  <>
                                    <span className="text-gray-400">Silica Fume</span>
                                    <span className="text-gray-700">{p.mix_design.silica_fume_content} kg/m³</span>
                                  </>
                                )}
                                {p.mix_design.water_content > 0 && (
                                  <>
                                    <span className="text-gray-400">Water</span>
                                    <span className="text-gray-700">{p.mix_design.water_content} kg/m³</span>
                                  </>
                                )}
                                {p.mix_design.wc_ratio > 0 && (
                                  <>
                                    <span className="text-gray-400">w/c ratio</span>
                                    <span className="text-gray-700 font-medium">{p.mix_design.wc_ratio}</span>
                                  </>
                                )}
                                {p.mix_design.scm_percentage > 0 && (
                                  <>
                                    <span className="text-gray-400">SCM %</span>
                                    <span className="text-gray-700">{p.mix_design.scm_percentage}%</span>
                                  </>
                                )}
                                {p.mix_design.total_cementitious > 0 && (
                                  <>
                                    <span className="text-gray-400">Total CM</span>
                                    <span className="text-gray-700">{p.mix_design.total_cementitious} kg/m³</span>
                                  </>
                                )}
                                {p.mix_design.coarse_aggregate > 0 && (
                                  <>
                                    <span className="text-gray-400">Coarse Agg.</span>
                                    <span className="text-gray-700">{p.mix_design.coarse_aggregate} kg/m³</span>
                                  </>
                                )}
                                {p.mix_design.fine_aggregate > 0 && (
                                  <>
                                    <span className="text-gray-400">Fine Agg.</span>
                                    <span className="text-gray-700">{p.mix_design.fine_aggregate} kg/m³</span>
                                  </>
                                )}
                                {p.mix_design.admixture_type && (
                                  <>
                                    <span className="text-gray-400">Admixture</span>
                                    <span className="text-gray-700">{p.mix_design.admixture_type} {p.mix_design.admixture_dosage && `(${p.mix_design.admixture_dosage})`}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t pt-3">
                  <a href={`mailto:${selectedUser.email}?subject=Your Identity Testing Checker account - Durable Designs`} className="block">
                    <Button className="w-full bg-green-600 hover:bg-green-700">
                      <Mail className="w-4 h-4 mr-2" /> Contact User
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a user to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;

/* ── v17: Rate Limit Admin Panel ───────────────────────────────────── */
const CALC_CONFIG = [
  { key: 'corrosivity', label: 'Corrosivity (ISO 9223)',           accent: 'emerald' },
  { key: 'carbonation', label: 'Carbonation (EN 1992)',            accent: 'blue' },
  { key: 'thermal',     label: 'Mass-concrete temperature predictor', accent: 'orange' },
];

const RateLimitPanel = ({ settings, error, updating, onUpdate, onRefresh }) => {
  // Only show blocking error card when we truly have nothing to render.
  if (error && !settings) {
    return (
      <div
        className="p-6 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800 space-y-3"
        data-testid="admin-rate-limits-error"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-900">Rate-limit settings unavailable</div>
            <div className="mt-1 text-red-800">{error}</div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="rate-limits-retry">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
        </Button>
      </div>
    );
  }
  if (!settings) {
    return (
      <div className="p-8 text-center text-sm text-gray-500" data-testid="admin-rate-limits-loading">
        Loading rate-limit settings…
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="admin-rate-limits-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Calculator rate limits</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Toggle per-IP daily limits on the three public calculators.
            When disabled, lookups are unlimited. Preloaded cities in the
            corrosivity calculator are always exempt regardless of this setting.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="rate-limits-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {error && (
        <div
          className="flex items-start gap-3 p-3 rounded-md border border-red-200 bg-red-50 text-xs text-red-800"
          data-testid="admin-rate-limits-save-error"
        >
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">{error}</div>
        </div>
      )}

      {CALC_CONFIG.map((calc) => {
        const cfg = settings[calc.key] || {};
        return (
          <Card key={calc.key} data-testid={`rate-limit-card-${calc.key}`}>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{calc.label}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {cfg.enabled ? (
                      <>Rate limiting <strong className="text-red-700">ON</strong> — {calc.key === 'corrosivity'
                        ? `${cfg.offshore_limit ?? 3}/day offshore, ${cfg.new_cams_limit ?? 2}/day new CAMS fetches`
                        : `${cfg.limit ?? 3}/day per IP`}</>
                    ) : (
                      <>Rate limiting <strong className="text-emerald-700">OFF</strong> — unlimited lookups</>
                    )}
                  </p>
                </div>
                <label className={`inline-flex items-center cursor-pointer ${updating === calc.key ? 'opacity-60 pointer-events-none' : ''}`}>
                  <input
                    type="checkbox"
                    checked={!!cfg.enabled}
                    onChange={(e) => onUpdate(calc.key, { enabled: e.target.checked })}
                    data-testid={`rate-limit-toggle-${calc.key}`}
                    disabled={updating === calc.key}
                    className="sr-only peer"
                  />
                  <span className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600" />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {updating === calc.key
                      ? 'Saving…'
                      : cfg.enabled
                      ? 'Enabled'
                      : 'Disabled'}
                  </span>
                </label>
              </div>

              {cfg.enabled && (
                <div className={`grid gap-3 pt-3 border-t border-gray-100 ${calc.key === 'corrosivity' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                  {calc.key === 'corrosivity' ? (
                    <>
                      <NumberField
                        label="Offshore lookups / IP / day"
                        testId={`rate-limit-offshore-${calc.key}`}
                        value={cfg.offshore_limit ?? 3}
                        onCommit={(n) => onUpdate(calc.key, { offshore_limit: n })}
                      />
                      <NumberField
                        label="New CAMS grid-cell fetches / IP / day"
                        testId={`rate-limit-newcams-${calc.key}`}
                        value={cfg.new_cams_limit ?? 2}
                        onCommit={(n) => onUpdate(calc.key, { new_cams_limit: n })}
                      />
                    </>
                  ) : (
                    <NumberField
                      label="Lookups / IP / day"
                      testId={`rate-limit-limit-${calc.key}`}
                      value={cfg.limit ?? 3}
                      onCommit={(n) => onUpdate(calc.key, { limit: n })}
                    />
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const NumberField = ({ label, value, onCommit, testId }) => {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <label className="flex items-center gap-3 text-sm text-gray-700">
      <span className="flex-1">{label}</span>
      <input
        type="number"
        min={0}
        max={1000}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          const n = Math.max(0, parseInt(v, 10) || 0);
          if (n !== value) onCommit(n);
        }}
        data-testid={testId}
        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
      />
    </label>
  );
};

/* ── Subscribers Panel ─────────────────────────────────────────────── */
const SubscribersPanel = ({ subscribers, storedPassword, onRefresh }) => {
  const verified = subscribers.filter(s => s.verified);
  const unverified = subscribers.filter(s => !s.verified);
  const [notifyForm, setNotifyForm] = useState({ title: '', url: '', excerpt: '' });
  const [notifyStatus, setNotifyStatus] = useState('idle');

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!notifyForm.title || !notifyForm.url) return;
    setNotifyStatus('sending');
    try {
      const res = await fetch(`${API_URL}/api/admin/notify-subscribers?password=${encodeURIComponent(storedPassword)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          article_title: notifyForm.title,
          article_url: notifyForm.url,
          article_excerpt: notifyForm.excerpt,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setNotifyStatus(`Sent to ${data.count} subscriber(s)`);
        setNotifyForm({ title: '', url: '', excerpt: '' });
      } else {
        const err = await res.json().catch(() => ({}));
        setNotifyStatus(err.detail || 'Failed to send');
      }
    } catch {
      setNotifyStatus('Network error');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-purple-600" /> Newsletter Subscribers
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            {verified.length} verified · {unverified.length} pending confirmation
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {subscribers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Newspaper className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No subscribers yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Email</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Status</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Subscribed</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Verified</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((s, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50" data-testid={`subscriber-row-${i}`}>
                      <td className="px-4 py-2.5 text-xs font-medium">{s.email}</td>
                      <td className="px-4 py-2.5">
                        {s.verified ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            <CheckCircle className="w-3 h-3" /> Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                            <Clock className="w-3 h-3" /> Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(s.subscribed_at)}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">{s.verified ? formatDate(s.verified_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Send className="w-5 h-5 text-purple-600" /> Send Article Notification
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Notify all {verified.length} verified subscriber{verified.length !== 1 ? 's' : ''} about a new article.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleNotify} className="space-y-4 max-w-lg" data-testid="notify-form">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Article Title *</label>
              <input
                type="text"
                value={notifyForm.title}
                onChange={(e) => setNotifyForm(p => ({ ...p, title: e.target.value }))}
                required
                placeholder="e.g. Low-Carbon Concrete — Part 2"
                className="w-full px-3 py-2 border border-gray-200 text-sm outline-none focus:border-green-500 transition-colors"
                data-testid="notify-title-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Article URL *</label>
              <input
                type="url"
                value={notifyForm.url}
                onChange={(e) => setNotifyForm(p => ({ ...p, url: e.target.value }))}
                required
                placeholder="https://linkedin.com/pulse/..."
                className="w-full px-3 py-2 border border-gray-200 text-sm outline-none focus:border-green-500 transition-colors"
                data-testid="notify-url-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Excerpt (optional)</label>
              <textarea
                value={notifyForm.excerpt}
                onChange={(e) => setNotifyForm(p => ({ ...p, excerpt: e.target.value }))}
                rows={2}
                placeholder="Brief description of the article..."
                className="w-full px-3 py-2 border border-gray-200 text-sm outline-none focus:border-green-500 transition-colors resize-none"
                data-testid="notify-excerpt-input"
              />
            </div>
            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={notifyStatus === 'sending' || verified.length === 0}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white px-5 py-2.5 text-sm font-medium tracking-wide transition-colors inline-flex items-center gap-2"
                data-testid="notify-submit-btn"
              >
                <Send className="w-4 h-4" />
                {notifyStatus === 'sending' ? 'Sending...' : `Notify ${verified.length} Subscriber${verified.length !== 1 ? 's' : ''}`}
              </button>
              {notifyStatus !== 'idle' && notifyStatus !== 'sending' && (
                <span className="text-sm text-gray-600" data-testid="notify-status">{notifyStatus}</span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
